import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Trash2, Loader2, Settings as SettingsIcon, Users as TeamIcon, UserSquare2 as UsersIcon, Send, Save, Plus, BarChart3, Image as ImageLucide, HelpCircle, BookOpen, Handshake, MapPinned, Building2 as ProvidersIcon } from "lucide-react";
import { toast } from "sonner";
import FleetUtilizationPanel from "@/components/admin/FleetUtilizationPanel";
import AdminFleetHealthChip from "@/components/admin/AdminFleetHealthChip";
import AdminFleetHealthSummaryStrip from "@/components/admin/AdminFleetHealthSummaryStrip";
import AdminSeoHealthCard from "@/components/admin/AdminSeoHealthCard";
import CountryHeroImagesPanel from "@/components/admin/CountryHeroImagesPanel";
import AdminDestinationCardsPanel from "@/components/admin/AdminDestinationCardsPanel";
import AdminFaqPanel from "@/components/admin/AdminFaqPanel";
import AdminBlogPanel from "@/components/admin/AdminBlogPanel";
import AdminPartnersPanel from "@/components/admin/AdminPartnersPanel";
import AdminLocationsPanel from "@/components/admin/AdminLocationsPanel";
import AdminCustomerRoster from "@/components/admin/AdminCustomerRoster";
import AdminVendorRoster from "@/components/admin/AdminVendorRoster";
import AdminWidgetAttributionTile from "@/components/admin/AdminWidgetAttributionTile";

/**
 * Admin Operations panel — three tabs surfacing the platform-level admin endpoints
 * added in iter26:
 *   • Settings    → GET/PATCH /admin/settings (brand name, support email, commission %)
 *   • Team        → CRUD /admin/agents (sub-admin / agent accounts with permission flags)
 *   • Notifications → POST /admin/notifications + GET /admin/notifications (audit log)
 *
 * Designed to slot below the existing 'All bookings' section on AdminDashboard so admins
 * have one screen with everything platform-related.
 */
export default function AdminOpsPanel() {
  const [activeTab, setActiveTab] = useState("settings");
  // Iter 67ef — IDs handed from the Customer Roster into the
  // Notifications tab. Lifting state here keeps the data hand-off
  // declarative — we just pass it as a prop, no context / portal.
  const [pickedIds, setPickedIds] = useState(null);
  return (
    <div className="rounded-3xl border border-border bg-white p-6 mt-12" data-testid="admin-ops-panel">
      <h2 className="font-display text-2xl font-medium mb-4">Platform operations</h2>
      {/* Iter 67da — always-visible Fleet-health summary chip above the tabs.
          Lets admins eyeball the platform's YTD net + bucket counts +
          breach count without clicking into the Fleet tab. Tapping the
          strip switches to the Fleet tab for the full drill-down. */}
      <AdminFleetHealthSummaryStrip onOpenFleetTab={() => setActiveTab("fleet")} />
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Iter 67de — mobile-friendly menu (<sm). Grid of colored icon
            cards instead of cramped horizontal tabs. Above sm the
            original TabsList renders inline. */}
        <_MobileOpsMenu activeTab={activeTab} setActiveTab={setActiveTab} />
        <TabsList className="hidden sm:flex flex-wrap h-auto gap-1.5 bg-transparent p-0">
          {/* Iter 67du — coloured triggers on desktop too. Each trigger uses
              its mobile-tile palette (matched via the static `bg`/`fg`/`ring`
              class strings on `_MOBILE_OPS_TILES`). Active state overrides
              with the brand black/amber. */}
          {_MOBILE_OPS_TILES.map(({ value, icon: Icon, label, bg, fg, ring }) => (
            <TabsTrigger
              key={value}
              value={value}
              data-testid={`admin-ops-tab-${value}`}
              className={`group h-9 px-3 rounded-full border ${bg} ${fg} ring-1 ${ring} transition hover:shadow-sm data-[state=active]:bg-neutral-900 data-[state=active]:text-amber-400 data-[state=active]:border-neutral-900 data-[state=active]:ring-amber-500/30 data-[state=active]:shadow-md`}
            >
              <Icon className="h-3.5 w-3.5 mr-1.5" />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="settings" className="mt-4">
          <div className="space-y-6">
            <SettingsTab />
            <AdminSeoHealthCard />
          </div>
        </TabsContent>
        <TabsContent value="team" className="mt-4"><TeamTab /></TabsContent>
        <TabsContent value="notifications" className="mt-4">
          <NotificationsTab initialIds={pickedIds} />
        </TabsContent>
        <TabsContent value="customers" className="mt-4">
          <AdminCustomerRoster
            onBroadcastSelected={(ids) => {
              // Iter 67ef — hand off to the Notifications tab via lifted
              // state. The audience selector pre-fills "Hand-picked (N)"
              // and a blue banner explains the selection. Clipboard copy
              // kept as a fallback for ops who prefer pasting into other
              // tools.
              setPickedIds(ids);
              setActiveTab("notifications");
              toast.success(`${ids.length} customer${ids.length === 1 ? "" : "s"} loaded into the broadcast audience.`);
              try {
                navigator.clipboard?.writeText(ids.join(","));
              } catch { /* clipboard blocked — manual copy fallback */ }
            }}
          />
        </TabsContent>
        <TabsContent value="providers" className="mt-4">
          <AdminVendorRoster
            onBroadcastSelected={(ids) => {
              // Iter 67eh — vendor hand-off mirrors the customer roster.
              // Same `ids:` audience format on the backend; the banner
              // copy explains who's about to be emailed.
              setPickedIds(ids);
              setActiveTab("notifications");
              toast.success(`${ids.length} vendor${ids.length === 1 ? "" : "s"} loaded into the broadcast audience.`);
              try {
                navigator.clipboard?.writeText(ids.join(","));
              } catch { /* clipboard blocked — manual copy fallback */ }
            }}
          />
        </TabsContent>
        <TabsContent value="fleet" className="mt-4">
          <div className="space-y-6">
            <AdminFleetHealthChip />
            <FleetUtilizationPanel />
            {/* Iter 67fc — widget vs organic attribution analytics. Lives in the Fleet
                tab because that's the existing home for platform-level KPIs / charts. */}
            <AdminWidgetAttributionTile />
          </div>
        </TabsContent>
        <TabsContent value="hero" className="mt-4">
          <div className="space-y-10">
            <CountryHeroImagesPanel />
            {/* Iter 79 — homepage "Top Destinations" cards editor. */}
            <div className="border-t border-border pt-8">
              <AdminDestinationCardsPanel />
            </div>
          </div>
        </TabsContent>
        <TabsContent value="blog" className="mt-4"><AdminBlogPanel /></TabsContent>
        <TabsContent value="faq" className="mt-4"><AdminFaqPanel /></TabsContent>
        <TabsContent value="partners" className="mt-4"><AdminPartnersPanel /></TabsContent>
        <TabsContent value="locations" className="mt-4"><AdminLocationsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

// Iter 67de — mobile ops menu. Below `sm` (640 px) the original
// TabsList becomes a horizontal scroll bar of cramped pills. Replace it
// with a 3-column grid of colored icon tiles so each section gets a
// pleasant touch target plus an instantly-recognisable colour code.
const _MOBILE_OPS_TILES = [
  { value: "settings",       icon: SettingsIcon, label: "Settings",      bg: "bg-slate-100",    fg: "text-slate-700",    ring: "ring-slate-200",    active: "from-slate-700 to-slate-900" },
  { value: "team",           icon: TeamIcon,     label: "Team",          bg: "bg-blue-50",      fg: "text-blue-700",     ring: "ring-blue-200",     active: "from-blue-500 to-blue-700" },
  { value: "notifications",  icon: Send,         label: "Notifications", bg: "bg-violet-50",    fg: "text-violet-700",   ring: "ring-violet-200",   active: "from-violet-500 to-violet-700" },
  { value: "customers",      icon: UsersIcon,    label: "Customers",     bg: "bg-blue-50",      fg: "text-blue-700",     ring: "ring-blue-200",     active: "from-blue-500 to-blue-700" },
  { value: "providers",      icon: ProvidersIcon, label: "Providers",    bg: "bg-emerald-50",   fg: "text-emerald-700",  ring: "ring-emerald-200",  active: "from-emerald-500 to-emerald-700" },
  { value: "fleet",          icon: BarChart3,    label: "Fleet",         bg: "bg-teal-50",      fg: "text-teal-700",     ring: "ring-teal-200",     active: "from-teal-500 to-teal-700" },
  { value: "hero",           icon: ImageLucide,  label: "Hero",          bg: "bg-pink-50",      fg: "text-pink-700",     ring: "ring-pink-200",     active: "from-pink-500 to-pink-700" },
  { value: "blog",           icon: BookOpen,     label: "Blog",          bg: "bg-orange-50",    fg: "text-orange-700",   ring: "ring-orange-200",   active: "from-orange-500 to-orange-700" },
  { value: "faq",            icon: HelpCircle,   label: "FAQ",           bg: "bg-amber-50",     fg: "text-amber-700",    ring: "ring-amber-200",    active: "from-amber-500 to-amber-700" },
  { value: "partners",       icon: Handshake,    label: "Partners",      bg: "bg-emerald-50",   fg: "text-emerald-700",  ring: "ring-emerald-200",  active: "from-emerald-500 to-emerald-700" },
  { value: "locations",      icon: MapPinned,    label: "Locations",     bg: "bg-cyan-50",      fg: "text-cyan-700",     ring: "ring-cyan-200",     active: "from-cyan-500 to-cyan-700" },
];

function _MobileOpsMenu({ activeTab, setActiveTab }) {
  return (
    <div
      className="sm:hidden grid grid-cols-3 gap-2.5 mb-4"
      role="tablist"
      data-testid="admin-ops-mobile-menu"
    >
      {_MOBILE_OPS_TILES.map(({ value, icon: Icon, label, bg, fg, ring, active }) => {
        const isActive = activeTab === value;
        return (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => setActiveTab(value)}
            className={`rounded-2xl px-2 py-3 ring-1 transition flex flex-col items-center gap-1.5 ${
              isActive
                ? `bg-gradient-to-br ${active} text-white ring-white/20 shadow-lg`
                : `${bg} ${fg} ${ring} hover:shadow-md`
            }`}
            data-testid={`admin-ops-mobile-tile-${value}`}
          >
            <span
              className={`h-9 w-9 rounded-xl flex items-center justify-center ${
                isActive ? "bg-white/15" : "bg-white/70 backdrop-blur"
              }`}
            >
              <Icon className="h-4 w-4" />
            </span>
            <span className={`text-[11px] font-bold uppercase tracking-wider truncate w-full text-center ${
              isActive ? "text-white" : ""
            }`}>
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
}


export function SettingsTab({ canEdit = true }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <div className="text-sm text-neutral-500">Loading settings…</div>;
  const set = (k) => (v) => setS({ ...s, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings", {
        brand_name: s.brand_name || null,
        support_email: s.support_email || null,
        commission_percent: Number(s.commission_percent) || 0,
        contact_phone: s.contact_phone || null,
        public_announcement: s.public_announcement || null,
      });
      setS(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl" data-testid="admin-settings-form">
      <div><Label>Brand name</Label><Input disabled={!canEdit} className="mt-1 rounded-xl" value={s.brand_name || ""} onChange={(e) => set("brand_name")(e.target.value)} data-testid="settings-brand-name" /></div>
      <div><Label>Support email</Label><Input disabled={!canEdit} type="email" className="mt-1 rounded-xl" value={s.support_email || ""} onChange={(e) => set("support_email")(e.target.value)} data-testid="settings-support-email" /></div>
      <div><Label>Commission % (0–50)</Label><Input disabled={!canEdit} type="number" min="0" max="50" step="0.5" className="mt-1 rounded-xl" value={s.commission_percent ?? ""} onChange={(e) => set("commission_percent")(e.target.value)} data-testid="settings-commission" /></div>
      <div><Label>Contact phone</Label><Input disabled={!canEdit} className="mt-1 rounded-xl" value={s.contact_phone || ""} onChange={(e) => set("contact_phone")(e.target.value)} data-testid="settings-contact-phone" /></div>
      <div className="md:col-span-2"><Label>Public announcement (top banner)</Label><Textarea disabled={!canEdit} rows={2} className="mt-1 rounded-xl" value={s.public_announcement || ""} onChange={(e) => set("public_announcement")(e.target.value)} placeholder="Optional — shows on the homepage banner" data-testid="settings-announcement" /></div>
      {canEdit ? (
        <div className="md:col-span-2"><Button onClick={save} disabled={saving} className="rounded-full" data-testid="settings-save">{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save settings</Button></div>
      ) : (
        <div className="md:col-span-2 text-xs text-neutral-500">Read-only — ask an admin to grant <code className="px-1 bg-neutral-100 rounded">edit_platform_settings</code> to make changes.</div>
      )}
    </div>
  );
}

function TeamTab() {
  const [agents, setAgents] = useState([]);
  const [perms, setPerms] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", permissions: [] });
  const load = () => {
    api.get("/admin/agents").then((r) => setAgents(r.data)).catch(() => {});
    api.get("/admin/agents/permissions").then((r) => setPerms(r.data.permissions)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.email || !form.password || !form.name) { toast.error("Email + name + password are required"); return; }
    setCreating(true);
    try {
      await api.post("/admin/agents", form);
      toast.success("Agent created");
      setForm({ email: "", name: "", password: "", permissions: [] });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setCreating(false); }
  };
  const togglePerm = (p) => setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));
  const remove = async (id) => {
    if (!window.confirm("Remove this agent?")) return;
    await api.delete(`/admin/agents/${id}`);
    toast.success("Agent removed");
    load();
  };
  const toggleActive = async (a) => {
    await api.patch(`/admin/agents/${a.id}`, { is_active: !a.is_active });
    load();
  };
  return (
    <div className="space-y-6" data-testid="admin-team-tab">
      <div className="rounded-2xl border border-border p-4 bg-neutral-50/50">
        <div className="text-sm font-semibold mb-3 flex items-center gap-1.5"><Plus className="h-4 w-4" /> Create agent</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Input placeholder="Email" type="email" className="rounded-xl" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-agent-email" />
          <Input placeholder="Full name" className="rounded-xl" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-agent-name" />
          <Input placeholder="Temporary password" type="text" className="rounded-xl" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-agent-password" />
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid="new-agent-permissions">
          {perms.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => togglePerm(p)}
              className={`px-2.5 py-1 text-xs rounded-full border transition ${form.permissions.includes(p) ? "bg-blue-600 text-white border-blue-600" : "bg-white border-border text-neutral-600 hover:border-neutral-400"}`}
              data-testid={`perm-${p}`}
            >
              {p.replace(/_/g, " ")}
            </button>
          ))}
        </div>
        <Button onClick={create} disabled={creating} className="rounded-full mt-3 bg-blue-600 hover:bg-blue-700" data-testid="create-agent">
          {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}Create agent
        </Button>
      </div>

      <div data-testid="agent-list">
        {agents.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-8 text-center text-neutral-500 text-sm">No agents yet.</div>
        ) : agents.map((a) => (
          <div key={a.id} className="rounded-xl border border-border p-4 mb-2 flex items-center justify-between gap-3" data-testid={`agent-row-${a.id}`}>
            <div className="min-w-0">
              <div className="font-medium">{a.name} <span className="text-neutral-400 text-sm">· {a.email}</span></div>
              <div className="flex flex-wrap gap-1 mt-1">
                {(a.permissions || []).map((p) => <Badge key={p} variant="secondary" className="text-[10px]">{p.replace(/_/g, " ")}</Badge>)}
                {(a.permissions || []).length === 0 && <span className="text-xs text-neutral-400">No permissions</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant={a.is_active ? "default" : "secondary"}>{a.is_active ? "Active" : "Disabled"}</Badge>
              <Button size="sm" variant="ghost" onClick={() => toggleActive(a)} className="rounded-full" data-testid={`toggle-${a.id}`}>{a.is_active ? "Disable" : "Enable"}</Button>
              <Button size="sm" variant="ghost" onClick={() => remove(a.id)} className="rounded-full text-rose-600 hover:text-rose-700" data-testid={`delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function NotificationsTab({ initialIds = null, transactional: initialTxn = false } = {}) {
  const [history, setHistory] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  // Iter 67ef — when the caller hands us a non-empty `initialIds` array
  // (e.g. from the Customers roster) we synthesise an `ids:` audience and
  // surface a banner so the admin sees who they're about to email.
  const [audience, setAudience] = useState(
    Array.isArray(initialIds) && initialIds.length > 0 ? `ids:${initialIds.join(",")}` : "all_customers"
  );
  const [pickedIds, setPickedIds] = useState(Array.isArray(initialIds) ? initialIds : []);
  const [transactional, setTransactional] = useState(!!initialTxn);
  const [sending, setSending] = useState(false);
  // Iter 67eg — saved audiences (reusable broadcast segments). Wired to
  // the `/api/admin/audiences` CRUD endpoints. `activeSavedId` tracks
  // which saved row (if any) is currently applied so the touch counter
  // only bumps when an admin sends a broadcast that originated from a
  // saved segment.
  const [savedAudiences, setSavedAudiences] = useState([]);
  const [activeSavedId, setActiveSavedId] = useState(null);
  const [savePromptOpen, setSavePromptOpen] = useState(false);
  const [savePromptName, setSavePromptName] = useState("");
  const load = () => api.get("/admin/notifications").then((r) => setHistory(r.data)).catch(() => {});
  const loadSaved = () => api.get("/admin/audiences").then((r) => setSavedAudiences(r.data || [])).catch(() => {});
  useEffect(() => { load(); loadSaved(); }, []);

  // React to roster→notifications hand-off mid-mount. Without this, opening
  // the Notifications tab from the Customers tab a second time wouldn't
  // update the pre-filled audience because the component is mounted once.
  useEffect(() => {
    if (Array.isArray(initialIds) && initialIds.length > 0) {
      setAudience(`ids:${initialIds.join(",")}`);
      setPickedIds(initialIds);
    }
  }, [initialIds]);

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject + body required"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/notifications", { subject, body, audience, transactional });
      toast.success(`Queued for ${data.recipient_count} recipient${data.recipient_count === 1 ? "" : "s"}`);
      // Iter 67eg — bump the saved-audience usage counter (if any) so
      // the picker can prioritise frequent segments. Fire-and-forget.
      if (activeSavedId) {
        api.post(`/admin/audiences/${activeSavedId}/touch`).catch(() => {});
      }
      setSubject(""); setBody("");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  // Iter 67eg — save/load/delete persisted segments.
  const applySaved = (aud) => {
    setActiveSavedId(aud.id);
    setAudience(aud.audience);
    if (aud.audience.startsWith("ids:")) {
      setPickedIds(aud.audience.slice(4).split(",").filter(Boolean));
    } else {
      setPickedIds([]);
    }
    toast.success(`Loaded audience: ${aud.name}`);
  };
  const saveAsAudience = async () => {
    const name = savePromptName.trim();
    if (!name) { toast.error("Give the audience a name"); return; }
    try {
      await api.post("/admin/audiences", { name, audience });
      toast.success(`Saved "${name}"`);
      setSavePromptName("");
      setSavePromptOpen(false);
      loadSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteSavedAudience = async (id, name) => {
    if (!window.confirm(`Delete saved audience "${name}"?`)) return;
    try {
      await api.delete(`/admin/audiences/${id}`);
      toast.success(`Removed "${name}"`);
      if (activeSavedId === id) setActiveSavedId(null);
      loadSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const clearPicked = () => {
    setPickedIds([]);
    setAudience("all_customers");
  };
  return (
    <div className="space-y-6" data-testid="admin-notifications-tab">
      {/* Iter 67ef — hand-off banner when the audience is a hand-picked ID list. */}
      {pickedIds.length > 0 && audience.startsWith("ids:") && (
        <div className="rounded-2xl bg-blue-50 border border-blue-200 px-4 py-3 flex items-center justify-between gap-3" data-testid="notif-picked-banner">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-blue-700">
              Audience: hand-picked from Customer Roster
            </div>
            <div className="text-sm text-blue-900 mt-0.5">
              <strong>{pickedIds.length}</strong> recipient{pickedIds.length === 1 ? "" : "s"} selected ·
              GDPR opt-outs will still be skipped unless this is a transactional message.
            </div>
          </div>
          <button
            type="button"
            onClick={clearPicked}
            className="text-xs font-semibold text-blue-700 hover:text-blue-900 underline"
            data-testid="notif-picked-clear"
          >
            Clear selection
          </button>
        </div>
      )}

      <div className="rounded-2xl border border-border p-4 bg-neutral-50/50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Audience</Label>
            <Select value={audience} onValueChange={(v) => { setAudience(v); setActiveSavedId(null); if (!v.startsWith("ids:")) setPickedIds([]); }}>
              <SelectTrigger className="mt-1 rounded-xl" data-testid="notif-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                {pickedIds.length > 0 && (
                  <SelectItem value={`ids:${pickedIds.join(",")}`} data-testid="audience-picked">
                    Hand-picked ({pickedIds.length})
                  </SelectItem>
                )}
                <SelectItem value="all_customers" data-testid="audience-all-customers">All customers</SelectItem>
                <SelectItem value="all_vendors" data-testid="audience-all-vendors">All vendors / providers</SelectItem>
                <SelectItem value="all_users" data-testid="audience-all-users">All users (customers + vendors)</SelectItem>
                <SelectItem value="country:FR">🇫🇷 France only</SelectItem>
                <SelectItem value="country:DE">🇩🇪 Germany only</SelectItem>
                <SelectItem value="country:ES">🇪🇸 Spain only</SelectItem>
                <SelectItem value="country:IT">🇮🇹 Italy only</SelectItem>
                <SelectItem value="country:AL">🇦🇱 Albania only</SelectItem>
                <SelectItem value="country:XK">🇽🇰 Kosovo only</SelectItem>
                <SelectItem value="country:ME">🇲🇪 Montenegro only</SelectItem>
                <SelectItem value="country:MK">🇲🇰 North Macedonia only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label>Subject</Label><Input className="mt-1 rounded-xl" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. New seasonal pricing kicks in May 1" data-testid="notif-subject" /></div>
          <div className="md:col-span-3"><Label>Body (HTML allowed)</Label><Textarea rows={6} className="mt-1 rounded-xl font-mono text-xs" value={body} onChange={(e) => setBody(e.target.value)} placeholder="<p>Hi there,</p>" data-testid="notif-body" /></div>
        </div>
        <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
          <label className="flex items-start gap-2 cursor-pointer select-none text-xs text-neutral-700 max-w-md" data-testid="notif-transactional-label">
            <input
              type="checkbox"
              checked={transactional}
              onChange={(e) => setTransactional(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
              data-testid="notif-transactional"
            />
            <span>
              <strong>Transactional</strong> — bypasses marketing opt-out.
              Only check this for safety / booking / legal notices (NOT for promotions).
            </span>
          </label>
          <Button onClick={send} disabled={sending} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="send-notif">
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send broadcast
          </Button>
        </div>
      </div>

      {/* Iter 67eg — Saved audience segments. Admins can persist any
          audience (country, ids:…, all_*) under a label so recurring
          campaigns don't have to re-pick the roster every time. */}
      <div className="rounded-2xl border border-border p-4" data-testid="saved-audiences">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold flex items-center gap-1.5"><Save className="h-3.5 w-3.5" />Saved audiences</div>
            <div className="text-xs text-neutral-500 mt-0.5">Reusable segments so you don't have to re-pick the same roster every campaign.</div>
          </div>
          {!savePromptOpen ? (
            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={() => { setSavePromptOpen(true); setSavePromptName(""); }}
              data-testid="save-audience-open"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />Save current as audience
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Input
                placeholder="e.g. France VIP renters"
                value={savePromptName}
                onChange={(e) => setSavePromptName(e.target.value)}
                className="rounded-full h-9 w-56 text-sm"
                data-testid="save-audience-name"
              />
              <Button size="sm" className="rounded-full bg-blue-600 hover:bg-blue-700" onClick={saveAsAudience} data-testid="save-audience-confirm">
                Save
              </Button>
              <Button size="sm" variant="ghost" className="rounded-full" onClick={() => { setSavePromptOpen(false); setSavePromptName(""); }} data-testid="save-audience-cancel">
                Cancel
              </Button>
            </div>
          )}
        </div>
        {savedAudiences.length === 0 ? (
          <div className="text-xs text-neutral-500 italic">No saved audiences yet.</div>
        ) : (
          <div className="flex flex-wrap gap-1.5" data-testid="saved-audience-list">
            {savedAudiences.map((aud) => {
              const isActive = activeSavedId === aud.id;
              return (
                <div
                  key={aud.id}
                  className={`group inline-flex items-center gap-1 rounded-full border pl-3 pr-1 py-1 text-xs transition ${
                    isActive
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-neutral-700 border-border hover:border-neutral-400"
                  }`}
                  data-testid={`saved-audience-${aud.id}`}
                >
                  <button
                    type="button"
                    onClick={() => applySaved(aud)}
                    className="font-medium"
                    data-testid={`saved-audience-apply-${aud.id}`}
                  >
                    {aud.name}
                    {aud.use_count ? <span className={`ml-1.5 text-[10px] ${isActive ? "text-blue-100" : "text-neutral-400"}`}>· {aud.use_count}×</span> : null}
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteSavedAudience(aud.id, aud.name)}
                    className={`h-5 w-5 rounded-full inline-flex items-center justify-center transition ${
                      isActive ? "hover:bg-blue-700" : "hover:bg-rose-50 text-rose-600"
                    }`}
                    title="Delete"
                    data-testid={`saved-audience-delete-${aud.id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div data-testid="notif-history">
        <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-2">Recent broadcasts</div>
        {history.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-6 text-center text-neutral-500 text-sm">No broadcasts yet.</div>
        ) : history.map((n) => (
          <div key={n.id} className="rounded-xl border border-border p-3 mb-2 flex items-center justify-between gap-3 text-sm" data-testid={`notif-row-${n.id}`}>
            <div className="min-w-0">
              <div className="font-medium truncate flex items-center gap-2">
                {n.subject}
                {n.transactional && (
                  <Badge variant="outline" className="text-[9px] uppercase tracking-wider border-amber-300 text-amber-700">Txn</Badge>
                )}
              </div>
              <div className="text-xs text-neutral-500">{n.audience} · {n.recipient_count} recipient{n.recipient_count === 1 ? "" : "s"} · {new Date(n.sent_at).toLocaleString()}</div>
            </div>
            <Badge variant={n.status === "sent" ? "default" : n.status === "sending" ? "secondary" : "outline"}>{n.status}</Badge>
          </div>
        ))}
      </div>
    </div>
  );
}
